/**
 * A página de fundação — o especímen do sistema de design, e a prova de que o contrato chegou até aqui.
 *
 * Esta página não é uma tela de domínio: é o especímen do sistema de design
 * mais o estado real do contrato gerado, lido de `@pauta/contract`. Se o
 * Pydantic mudar e o contrato não for regenerado, o número de entidades
 * abaixo muda junto — o app é o primeiro lugar onde a divergência aparece.
 *
 * As telas de domínio moram em `Cadastro.tsx`; a grade chega na Fase 1.
 */

import {
  CAMPOS_PESSOAIS,
  CRITERIOS,
  ENTIDADES_CONTRATO,
  PRESETS,
  VERSAO_CONTRATO,
} from '@pauta/contract'

import { Icon } from '../components/Icon'

const PAPEIS_DE_COR = [
  { token: '--paper', papel: 'fundo da aplicação' },
  { token: '--surface', papel: 'superfície elevada' },
  { token: '--ink', papel: 'texto primário' },
  { token: '--ink-2', papel: 'texto secundário' },
  { token: '--ink-3', papel: 'rótulo de 11px' },
  { token: '--rule', papel: 'régua — o fio' },
  { token: '--rule-strong', papel: 'régua de divisão' },
  { token: '--control-border', papel: 'fronteira de controle — 3:1' },
  { token: '--accent', papel: 'ação e seleção' },
]

const ESCALA = [
  { token: '--text-xl', px: '24px', uso: 'título de página', classe: 'esp-xl' },
  { token: '--text-lg', px: '19px', uso: 'título de seção', classe: 'esp-lg' },
  { token: '--text-md', px: '16px', uso: 'subtítulo', classe: 'esp-md' },
  { token: '--text-base', px: '14px', uso: 'corpo', classe: 'esp-base' },
  { token: '--text-sm', px: '13px', uso: 'texto de tabela', classe: 'esp-sm' },
  { token: '--text-xs', px: '12px', uso: 'célula da grade', classe: 'esp-xs' },
  { token: '--text-2xs', px: '11px', uso: 'rótulo em caixa alta', classe: 'esp-2xs' },
]

/** Disciplinas reais de uma grade brasileira, para provar a paleta no uso. */
const DISCIPLINAS = [
  'Matemática',
  'Português',
  'História',
  'Geografia',
  'Física',
  'Química',
  'Biologia',
  'Ed. Física',
  'Filosofia',
  'Arte',
]

export function Fundacao() {
  return (
    <>
      <header className="page-head">
        <h1>Fundação</h1>
        <p className="page-head__note">
          Fase 0 · sistema de design
          <br />
          nomes e escola são fictícios; os números vêm do contrato
        </p>
      </header>

      <p className="callout">
        <strong>O que esta página é.</strong> O vocabulário visual que as telas de cadastro e a
        grade vão herdar, mais o estado do contrato gerado. O mundo é uma <em>pauta</em>: a região
        se separa por régua, não por cartão, e o orçamento de cor pertence ao dado — disciplina,
        turma, estado — nunca à moldura.
      </p>

      {/* --- o contrato, lido de verdade ---------------------------------- */}
      <section className="section">
        <div className="section__head">
          <h2>O contrato</h2>
          <p>gerado do Pydantic · lido de @pauta/contract</p>
        </div>

        <ul className="ruled">
          <li>
            <span className="ruled__key">Versão</span>
            <span className="ruled__val mono">{VERSAO_CONTRATO}</span>
            <span className="ruled__note">viaja nos dois documentos</span>
          </li>
          <li>
            <span className="ruled__key">Entidades no contrato</span>
            <span className="ruled__val mono">{ENTIDADES_CONTRATO.length}</span>
            <span className="ruled__note">
              das 22 do modelo; Projeto, Membro, Cenário e Processamento não entram no solver
            </span>
          </li>
          <li>
            <span className="ruled__key">Critérios de qualidade</span>
            <span className="ruled__val mono">
              {CRITERIOS.definidos} / {CRITERIOS.alvo}
            </span>
            <span className="ruled__note">
              os 30 do URÂNIA mais os 2 do PowerCubus; pesos a calibrar contra grade real
            </span>
          </li>
          <li>
            <span className="ruled__key">Tipos de distribuição</span>
            <span className="ruled__val mono">{PRESETS.total}</span>
            <span className="ruled__note">
              dados, não código — nenhum <span className="mono">if</span> no solver conhece a letra
              “D”
            </span>
          </li>
          <li>
            <span className="ruled__key">Campos pessoais</span>
            <span className="ruled__val mono">{CAMPOS_PESSOAIS.length}</span>
            <span className="ruled__note">
              {CAMPOS_PESSOAIS.map((c) => `${c.entidade}.${c.campo}`).join(', ')}
            </span>
          </li>
        </ul>

        <div className="row">
          {ENTIDADES_CONTRATO.map((nome) => (
            <span className="chip" key={nome}>
              {nome}
            </span>
          ))}
        </div>
      </section>

      {/* --- tipografia --------------------------------------------------- */}
      <section className="section">
        <div className="section__head">
          <h2>Escala tipográfica</h2>
          <p>pilha de sistema · mono auto-hospedado para dado</p>
        </div>

        <ul className="ruled">
          {ESCALA.map((nivel) => (
            <li key={nivel.token}>
              <span className="ruled__key mono">{nivel.token}</span>
              <span className={`ruled__val ${nivel.classe}`}>Grade da 3ª série B</span>
              <span className="ruled__note">
                {nivel.px} · {nivel.uso}
              </span>
            </li>
          ))}
        </ul>

        <p className="callout">
          <strong>Por que sem webfont para texto.</strong> O maior tipo desta interface tem 24px —
          não há voz de display para terceirizar. E a rede pública inclui escola com máquina
          modesta e conexão ruim. O único webfont é o mono, porque ele carrega medida: horário,
          duração, contagem. <span className="mono">07:30 · 50 min · 4 aulas</span>
        </p>
      </section>

      {/* --- cor ---------------------------------------------------------- */}
      <section className="section">
        <div className="section__head">
          <h2>Papéis de cor</h2>
          <p>neutros mais um acento — o azul da régua do caderno</p>
        </div>

        <ul className="swatches">
          {PAPEIS_DE_COR.map((item) => (
            <li className="swatch" key={item.token}>
              <span
                className="swatch__chip"
                style={{ background: `var(${item.token})` }}
                aria-hidden
              />
              <span className="swatch__name">{item.token}</span>
              <span className="swatch__role">{item.papel}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* --- a paleta categórica ------------------------------------------ */}
      <section className="section">
        <div className="section__head">
          <h2>Disciplinas</h2>
          <p>onde o orçamento de cor é gasto de fato</p>
        </div>

        <div className="row">
          {DISCIPLINAS.map((nome, i) => (
            <span
              className="cat"
              key={nome}
              style={
                {
                  '--cat-wash': `var(--cat-${i + 1}-wash)`,
                  '--cat-ink': `var(--cat-${i + 1}-ink)`,
                  '--cat-rule': `var(--cat-${i + 1}-rule)`,
                } as React.CSSProperties
              }
            >
              {nome}
            </span>
          ))}
        </div>

        <p className="field__hint">
          Dez matizes em lightness e croma controlados, ordenados por separação máxima entre
          vizinhos — porque disciplinas adjacentes na grade precisam se distinguir lado a lado.
          Cada par lavagem + tinta passa 4.5:1, já que a etiqueta carrega texto dentro dela.
        </p>
      </section>

      {/* --- estados ------------------------------------------------------ */}
      <section className="section">
        <div className="section__head">
          <h2>Estados da grade</h2>
          <p>forma e texto além de cor, sempre</p>
        </div>

        <div className="row">
          <span className="state state--pendencia">
            <Icon name="pendencia" size={14} />
            3 aulas pendentes
          </span>
          <span className="state state--violacao">
            <Icon name="aviso" size={14} />
            Janela na terça
          </span>
          <span className="state state--fixada">
            <Icon name="fixada" size={14} />
            Fixada
          </span>
          <span className="state state--ok">
            <Icon name="ok" size={14} />
            Sem violação
          </span>
        </div>

        <p className="field__hint">
          Pendência é falha — aula sem lugar. Violação é escolha consciente do motor, admitida e
          marcada, e nunca aparece sem a sugestão de correção ao lado. Fixada não recebe cor
          própria: recebe hachura, porque uma cor a mais na grade é contraste roubado da
          disciplina.
        </p>
      </section>

      {/* --- controles ---------------------------------------------------- */}
      <section className="section">
        <div className="section__head">
          <h2>Controles</h2>
          <p>o que as telas de cadastro herdam</p>
        </div>

        <div className="row">
          <button type="button" className="btn btn--primary">
            <Icon name="processar" size={14} />
            Processar grade
          </button>
          <button type="button" className="btn btn--secondary">
            <Icon name="importar" size={14} />
            Importar
          </button>
          <button type="button" className="btn btn--ghost">
            Descartar
          </button>
          <button type="button" className="btn btn--secondary" disabled>
            Indisponível
          </button>
        </div>

        <div className="row" style={{ alignItems: 'flex-start' }}>
          <div className="field" style={{ width: '16rem' }}>
            <label className="field__label" htmlFor="esp-nome">
              Nome do professor
            </label>
            <input className="input" id="esp-nome" defaultValue="Ana Ribeiro" />
            <span className="field__hint">Abreviatura e código externo ficam ao lado.</span>
          </div>

          <div className="field" style={{ width: '10rem' }}>
            <label className="field__label" htmlFor="esp-aulas">
              Aulas semanais
            </label>
            <input
              className="input input--mono"
              id="esp-aulas"
              inputMode="numeric"
              defaultValue="4"
            />
          </div>

          <div className="field" style={{ width: '13rem' }}>
            <label className="field__label" htmlFor="esp-cpf">
              CPF
            </label>
            <input
              className="input input--mono"
              id="esp-cpf"
              aria-invalid="true"
              aria-describedby="esp-cpf-erro"
              defaultValue="111.111.111-11"
            />
            {/* Erro nomeia o problema E a recuperação. */}
            <span className="field__error" id="esp-cpf-erro">
              <Icon name="aviso" size={13} />
              CPF inválido. Confira os dígitos ou deixe em branco.
            </span>
          </div>
        </div>
      </section>

      {/* --- vazio -------------------------------------------------------- */}
      <section className="section">
        <div className="section__head">
          <h2>Estado vazio</h2>
          <p>o único momento em que a pessoa está olhando e não sabe o que fazer</p>
        </div>

        <div className="empty">
          <p className="empty__title">Nenhuma turma cadastrada ainda.</p>
          <p className="empty__body">
            Três caminhos levam dados para dentro: a planilha modelo, um export do URÂNIA — o{' '}
            <span className="mono">.xml</span> do UP ou o <span className="mono">.json</span> do U+
            — ou cadastro manual. Se a escola já usa outro sistema, começar pelo export poupa a
            redigitação inteira.
          </p>
          <div className="empty__actions">
            <button type="button" className="btn btn--primary">
              <Icon name="importar" size={14} />
              Importar planilha modelo
            </button>
            <button type="button" className="btn btn--secondary">
              Trazer do URÂNIA
            </button>
            <button type="button" className="btn btn--ghost">
              Cadastrar à mão
            </button>
          </div>
        </div>
      </section>
    </>
  )
}
